/*
 * The A4 invoice in the browser (owner, 2026-10-25; ADR-0138 amendment 8): laid out in a hidden
 * frame and printed by the browser or drawn into a PDF (html2canvas + jsPDF, loaded only when asked
 * for). Shared by the online console and the store's invoice page.
 */

/** A4 at 96 dpi: 210 mm wide. */
const A4_WIDTH_PX = 794;

/** The page's own @font-face rules with absolute addresses, so the frame uses the same font. */
export function fontCss(): string {
  const rules: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let list: CSSRuleList;
    try {
      list = sheet.cssRules;
    } catch {
      continue;
    }
    for (const rule of Array.from(list)) {
      if (!rule.cssText.startsWith('@font-face')) continue;
      const base = sheet.href ?? document.baseURI;
      rules.push(
        rule.cssText.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g, (_m, _q, url: string) =>
          url.startsWith('data:') ? `url(${url})` : `url("${new URL(url, base).href}")`,
        ),
      );
    }
  }
  return rules.join('\n');
}

/** Lays the document out in a hidden A4-wide frame and waits for its fonts and pictures. */
async function layOut(html: string): Promise<HTMLIFrameElement> {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  Object.assign(frame.style, {
    position: 'fixed',
    insetInlineStart: '-10000px',
    top: '0',
    width: `${A4_WIDTH_PX}px`,
    height: '1123px',
    border: '0',
    visibility: 'hidden',
  });
  const loaded = new Promise<void>((resolve) => frame.addEventListener('load', () => resolve()));
  frame.srcdoc = html;
  document.body.append(frame);
  await loaded;
  const doc = frame.contentDocument!;
  const ready = Promise.all([
    doc.fonts?.ready,
    doc.fonts?.load("400 16px 'IBM Plex Sans Arabic'", 'فاتورة').catch(() => undefined),
    doc.fonts?.load("700 16px 'IBM Plex Sans Arabic'", 'فاتورة').catch(() => undefined),
    ...Array.from(doc.images).map((img) =>
      img.complete
        ? undefined
        : new Promise((done) => {
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
          }),
    ),
  ]);
  // A picture or font that never answers must not hold the invoice back.
  await Promise.race([ready, new Promise((done) => setTimeout(done, 5000))]);
  return frame;
}

/** The browser's print dialog on the A4 document (or "Save as PDF" there). */
export async function printInvoice(html: string): Promise<void> {
  const frame = await layOut(html);
  const win = frame.contentWindow!;
  win.addEventListener('afterprint', () => frame.remove(), { once: true });
  win.focus();
  win.print();
  // Browsers without afterprint in frames: clean up a minute later.
  setTimeout(() => frame.remove(), 60_000);
}

/**
 * A PDF of the A4 document: drawn at twice the size for sharp text, cut into A4 pages between
 * rows (never through one).
 */
export async function downloadInvoicePdf(html: string, fileName: string): Promise<void> {
  (await invoicePdf(html)).save(fileName);
}

/** The invoice as a PDF file, for the share sheet (ADR-0138 amendment 7). */
export async function invoicePdfFile(html: string, fileName: string): Promise<File> {
  const blob = (await invoicePdf(html)).output('blob');
  return new File([blob], fileName, { type: 'application/pdf' });
}

async function invoicePdf(html: string) {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas-pro'),
    import('jspdf'),
  ]);
  const frame = await layOut(html);
  try {
    const page = frame.contentDocument!.querySelector<HTMLElement>('.page')!;
    const scale = 2;
    const canvas = await html2canvas(page, {
      scale,
      backgroundColor: '#ffffff',
      useCORS: true,
      windowWidth: A4_WIDTH_PX,
    });
    const pageHeightPx = Math.floor((page.offsetWidth * 297) / 210);
    // Where a page may end: under a table row or a block, measured from the top of the page.
    const top = page.getBoundingClientRect().top;
    const breaks = Array.from(page.querySelectorAll('tr, .boxes, .bottom, .foot, .head'))
      .map((el) => el.getBoundingClientRect().bottom - top)
      .sort((a, b) => a - b);
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    let from = 0;
    const total = page.scrollHeight;
    // A few pixels left over (rounding of 297 mm) are not a page.
    while (total - from > 4) {
      // What is left fits on this page (within the rounding of 297 mm): all of it.
      let to = total - from <= pageHeightPx + 4 ? total : from + pageHeightPx;
      if (to < total) {
        const fit = breaks.filter((b) => b > from + 40 && b <= to).at(-1);
        if (fit) to = fit;
      }
      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = Math.round((to - from) * scale);
      const ctx = slice.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, slice.width, slice.height);
      ctx.drawImage(canvas, 0, -Math.round(from * scale));
      if (from > 0) pdf.addPage();
      pdf.addImage(
        slice.toDataURL('image/jpeg', 0.92),
        'JPEG',
        0,
        0,
        210,
        ((to - from) * 210) / page.offsetWidth,
      );
      from = to;
    }
    return pdf;
  } finally {
    frame.remove();
  }
}
